<template>
  <div id="live-paper-trading">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Paper trading only — no real orders are placed. Entries/exits are simulated against live quotes for the configured watchlist during market hours (09:15–15:30 IST). Manage the watchlist and capital/risk settings under Backtests → Settings.
      </v-alert>

      <v-alert v-if="pollError" type="error" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Auto-refresh stopped: {{ pollError }}. Showing the last successfully loaded data.
        <v-btn variant="text" size="small" class="ml-2" @click="resumePolling">Retry</v-btn>
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Live Paper Trading</span>
          <span class="text-caption text-medium-emphasis">
            {{ pollHandle ? 'Auto-refreshes every 20s' : 'Auto-refresh stopped' }}{{ lastFetchedAt ? ` · last updated ${lastFetchedAt}` : '' }}
          </span>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="trades"
          item-value="code"
          fixed-header
          :items-per-page="-1"
          hide-default-footer
          class="custom-data-table"
        >
          <template v-slot:[`item.direction`]="{ item }">
            <TradeSideChip :direction="item.direction" />
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="statusColor(item.status)" size="small" variant="tonal">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>

          <template v-slot:[`item.partial_booked`]="{ item }">
            <div v-if="item.partial_booked || item.stage1_booked || item.stage2_booked" class="d-flex flex-column ga-1">
              <v-chip v-if="item.partial_booked" color="warning" size="small" variant="tonal">
                Partial @ {{ $helper.formatPrice(item.partial_exit_price) }}
              </v-chip>
              <v-chip v-if="item.stage1_booked" color="warning" size="small" variant="tonal">
                Stage 1 @ {{ $helper.formatPrice(item.stage1_exit_price) }}
              </v-chip>
              <v-chip v-if="item.stage2_booked" color="warning" size="small" variant="tonal">
                Stage 2 @ {{ $helper.formatPrice(item.stage2_exit_price) }}
              </v-chip>
            </div>
            <span v-else>—</span>
          </template>

          <template v-slot:[`item.entry_price`]="{ item }">{{ $helper.formatPrice(item.entry_price) }}</template>
          <template v-slot:[`item.stop_price`]="{ item }">
            <template v-if="item.stop_price">
              {{ $helper.formatPrice(item.stop_price) }}
              <div
                v-if="item.initial_stop_price !== null && item.initial_stop_price !== undefined && Number(item.initial_stop_price) !== Number(item.stop_price)"
                class="text-caption text-medium-emphasis"
              >
                was {{ $helper.formatPrice(item.initial_stop_price) }}
              </div>
            </template>
            <template v-else-if="item.shadow_stop_price">
              {{ $helper.formatPrice(item.shadow_stop_price) }}
              <div class="text-caption text-medium-emphasis">sim</div>
            </template>
            <span v-else>—</span>
          </template>
          <template v-slot:[`item.target_price`]="{ item }">
            <template v-if="item.target_price">{{ $helper.formatPrice(item.target_price) }}</template>
            <template v-else-if="item.shadow_target_price">
              {{ $helper.formatPrice(item.shadow_target_price) }}
              <div class="text-caption text-medium-emphasis">sim</div>
            </template>
            <span v-else>—</span>
          </template>
          <template v-slot:[`item.current_price`]="{ item }">{{ item.current_price ? $helper.formatPrice(item.current_price) : '—' }}</template>

          <template v-slot:[`item.quantity`]="{ item }">
            <span v-if="item.partial_booked || item.stage1_booked">{{ item.remaining_quantity }} / {{ item.quantity }}</span>
            <span v-else-if="item.quantity">{{ item.quantity }}</span>
            <template v-else-if="item.shadow_quantity">
              {{ item.shadow_quantity }}
              <div class="text-caption text-medium-emphasis">sim</div>
            </template>
            <span v-else>—</span>
          </template>
          <template v-slot:[`item.capital_allocated`]="{ item }">
            <template v-if="item.capital_allocated">{{ $helper.formatPrice(item.capital_allocated) }}</template>
            <template v-else-if="item.shadow_capital_allocated">
              {{ $helper.formatPrice(item.shadow_capital_allocated) }}
              <div class="text-caption text-medium-emphasis">sim</div>
            </template>
            <span v-else>—</span>
          </template>

          <template v-slot:[`item.today_confidence_pct`]="{ item }">{{ item.today_confidence_pct !== null && item.today_confidence_pct !== undefined ? `${item.today_confidence_pct}%` : '—' }}</template>
          <template v-slot:[`item.historical_win_rate_pct`]="{ item }">{{ item.historical_win_rate_pct !== null && item.historical_win_rate_pct !== undefined ? `${Number(item.historical_win_rate_pct).toFixed(1)}%` : '—' }}</template>
          <template v-slot:[`item.wilson_lower_bound_pct`]="{ item }">{{ item.wilson_lower_bound_pct !== null && item.wilson_lower_bound_pct !== undefined ? `${Number(item.wilson_lower_bound_pct).toFixed(1)}%` : '—' }}</template>
          <template v-slot:[`item.return_lower_bound_pct`]="{ item }">
            <span v-if="item.return_lower_bound_pct !== null && item.return_lower_bound_pct !== undefined" :class="Number(item.return_lower_bound_pct) >= 0 ? 'text-success' : 'text-error'">
              {{ Number(item.return_lower_bound_pct).toFixed(3) }}%
            </span>
            <span v-else>—</span>
          </template>
          <template v-slot:[`item.rank_position`]="{ item }">{{ item.rank_position || '—' }}</template>

          <template v-slot:[`item.pnl_pct`]="{ item }">
            <span
              v-if="item.pnl_pct !== null && item.pnl_pct !== undefined"
              :class="Number(item.pnl_pct) >= 0 ? 'text-success' : 'text-error'"
            >
              {{ Number(item.pnl_pct).toFixed(2) }}%
            </span>
            <span v-else-if="item.shadow_pnl_pct !== null && item.shadow_pnl_pct !== undefined" class="text-medium-emphasis">
              {{ Number(item.shadow_pnl_pct).toFixed(2) }}%
              <v-chip color="default" size="x-small" variant="tonal" class="ml-1">sim</v-chip>
            </span>
            <span v-else>—</span>
          </template>

          <template v-slot:[`item.pnl_rs`]="{ item }">
            <span
              v-if="item.pnl_rs !== null && item.pnl_rs !== undefined"
              :class="Number(item.pnl_rs) >= 0 ? 'text-success' : 'text-error'"
            >
              {{ $helper.formatPrice(item.pnl_rs) }}
            </span>
            <span v-else-if="item.shadow_pnl_rs !== null && item.shadow_pnl_rs !== undefined" class="text-medium-emphasis">
              {{ $helper.formatPrice(item.shadow_pnl_rs) }}
              <v-chip color="default" size="x-small" variant="tonal" class="ml-1">sim</v-chip>
            </span>
            <span v-else>—</span>
          </template>

          <template v-slot:[`item.last_tick_at`]="{ item }">
            {{ item.last_tick_at ? new Date(item.last_tick_at).toLocaleTimeString() : '—' }}
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center text-medium-emphasis">
              No paper trades yet today — this fills in once the market-hours scheduler starts ticking.
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import TradeSideChip from './TradeSideChip.vue';

export default {
  name: 'LivePaperTrading-page',
  components: { TradeSideChip },
  data() {
    return {
      trades: [],
      lastFetchedAt: null,
      pollHandle: null,
      pollError: null,
      headers: [
        { title: 'Code', key: 'code' },
        { title: 'Side', key: 'direction' },
        { title: 'Status', key: 'status' },
        { title: 'Booking', key: 'partial_booked' },
        { title: 'Entry', key: 'entry_price' },
        { title: 'Stop', key: 'stop_price' },
        { title: 'Target', key: 'target_price' },
        { title: 'Current', key: 'current_price' },
        { title: 'Qty', key: 'quantity' },
        { title: 'Capital', key: 'capital_allocated' },
        { title: 'P&L %', key: 'pnl_pct' },
        { title: 'P&L ₹', key: 'pnl_rs' },
        { title: 'Confidence', key: 'today_confidence_pct' },
        { title: 'Hist. Win %', key: 'historical_win_rate_pct' },
        { title: 'Wilson LB %', key: 'wilson_lower_bound_pct' },
        { title: 'Return LB %', key: 'return_lower_bound_pct' },
        { title: 'Rank', key: 'rank_position' },
        { title: 'Last Tick', key: 'last_tick_at' },
      ],
    };
  },
  mounted() {
    this.loadTrades();
    this.pollHandle = setInterval(this.loadTrades, 20000);
  },
  beforeUnmount() {
    if (this.pollHandle) clearInterval(this.pollHandle);
  },
  methods: {
    loadTrades() {
      fetchWrapper.get('api/gap-backtest/paper-trades').then((res) => {
        this.trades = res?.data || [];
        this.lastFetchedAt = new Date().toLocaleTimeString();
        this.pollError = null;
      }).catch((err) => {
        console.error('Failed to load paper trades:', err);
        // Stop polling on failure rather than retrying a broken endpoint
        // every 20s -- but surface it and null out pollHandle so the UI
        // (and resumePolling()) both know polling has actually stopped,
        // instead of silently freezing on stale data with no indication.
        if (this.pollHandle) {
          clearInterval(this.pollHandle);
          this.pollHandle = null;
        }
        this.pollError = typeof err === 'string' ? err : 'failed to load trades';
      });
    },
    resumePolling() {
      this.pollError = null;
      this.loadTrades();
      if (!this.pollHandle) this.pollHandle = setInterval(this.loadTrades, 20000);
    },
    statusColor(status) {
      return {
        open: 'info',
        won: 'success',
        lost: 'error',
        closed_flat: 'warning',
        no_signal: 'default',
        skipped_ranked_out: 'default',
      }[status] || 'default';
    },
    statusLabel(status) {
      return {
        open: 'Open',
        won: 'Won',
        lost: 'Lost',
        closed_flat: 'Closed Flat',
        no_signal: 'No Signal',
        skipped_ranked_out: 'Ranked Out',
      }[status] || status;
    },
  },
};
</script>
